/**
 * Константы и утилиты для работы со статусами заявок
 * 
 * ВАЖНО: Предпочтительно использовать переводы статусов из DTO заявок (поля status_label и status_variant),
 * которые приходят с бэкенда. Этот файл используется как fallback на случай, если бэкенд не вернет переводы.
 */

// Все возможные статусы заявок
export const REQUEST_STATUSES = {
    DRAFT: 'draft',
    SUBMITTED: 'submitted',
    IN_REVIEW: 'in_review',
    RETURNED: 'returned',
    APPROVED: 'approved',
    AWAITING_EXECUTOR: 'awaiting_executor',
    IN_PROGRESS: 'in_progress',
    PARTIALLY_COMPLETED: 'partially_completed',
    AWAITING_ACCEPTANCE: 'awaiting_acceptance',
    PARTIALLY_ACCEPTED: 'partially_accepted',
    COMPLETED: 'completed',
    REJECTED: 'rejected',
    CANCELLED: 'cancelled',
    OVERDUE: 'overdue'
};

// Текстовые представления статусов
export const STATUS_LABELS = {
    [REQUEST_STATUSES.DRAFT]: 'Черновик',
    [REQUEST_STATUSES.SUBMITTED]: 'Отправлена',
    [REQUEST_STATUSES.IN_REVIEW]: 'На рассмотрении',
    [REQUEST_STATUSES.RETURNED]: 'Возвращена на доработку',
    [REQUEST_STATUSES.APPROVED]: 'Согласована',
    [REQUEST_STATUSES.AWAITING_EXECUTOR]: 'Ожидание назначения исполнителя',
    [REQUEST_STATUSES.IN_PROGRESS]: 'В работе',
    [REQUEST_STATUSES.PARTIALLY_COMPLETED]: 'Частично выполнена',
    [REQUEST_STATUSES.AWAITING_ACCEPTANCE]: 'Ожидает приемки',
    [REQUEST_STATUSES.PARTIALLY_ACCEPTED]: 'Частично принята',
    [REQUEST_STATUSES.COMPLETED]: 'Выполнена',
    [REQUEST_STATUSES.REJECTED]: 'Отклонена',
    [REQUEST_STATUSES.CANCELLED]: 'Отменена',
    [REQUEST_STATUSES.OVERDUE]: 'Просрочена'
};

// Варианты цветов для Bootstrap badges
export const STATUS_VARIANTS = {
    [REQUEST_STATUSES.DRAFT]: 'secondary',
    [REQUEST_STATUSES.SUBMITTED]: 'info',
    [REQUEST_STATUSES.IN_REVIEW]: 'warning',
    [REQUEST_STATUSES.RETURNED]: 'warning',
    [REQUEST_STATUSES.APPROVED]: 'success',
    [REQUEST_STATUSES.AWAITING_EXECUTOR]: 'info',
    [REQUEST_STATUSES.IN_PROGRESS]: 'primary',
    [REQUEST_STATUSES.PARTIALLY_COMPLETED]: 'warning',
    [REQUEST_STATUSES.AWAITING_ACCEPTANCE]: 'info',
    [REQUEST_STATUSES.PARTIALLY_ACCEPTED]: 'info',
    [REQUEST_STATUSES.COMPLETED]: 'success',
    [REQUEST_STATUSES.REJECTED]: 'danger',
    [REQUEST_STATUSES.CANCELLED]: 'dark',
    [REQUEST_STATUSES.OVERDUE]: 'danger'
};

// Описания статусов
export const STATUS_DESCRIPTIONS = {
    [REQUEST_STATUSES.DRAFT]: 'Заявка в процессе заполнения',
    [REQUEST_STATUSES.SUBMITTED]: 'Заявка отправлена на согласование',
    [REQUEST_STATUSES.IN_REVIEW]: 'Заявка на этапе согласования',
    [REQUEST_STATUSES.RETURNED]: 'Согласующий вернул на доработку',
    [REQUEST_STATUSES.APPROVED]: 'Все согласования получены',
    [REQUEST_STATUSES.AWAITING_EXECUTOR]: 'Заявка ожидает назначения исполнителя',
    [REQUEST_STATUSES.IN_PROGRESS]: 'Исполнитель выполняет заявку',
    [REQUEST_STATUSES.PARTIALLY_COMPLETED]: 'Заявка частично выполнена',
    [REQUEST_STATUSES.AWAITING_ACCEPTANCE]: 'Ожидает подтверждения инициатором',
    [REQUEST_STATUSES.PARTIALLY_ACCEPTED]: 'Заявка частично принята',
    [REQUEST_STATUSES.COMPLETED]: 'Заявка успешно завершена',
    [REQUEST_STATUSES.REJECTED]: 'Заявка отклонена',
    [REQUEST_STATUSES.CANCELLED]: 'Заявка отменена',
    [REQUEST_STATUSES.OVERDUE]: 'Нарушены регламентные сроки'
};

/**
 * Получить текстовое представление статуса
 * @param {string} status - код статуса
 * @returns {string} - текст статуса
 */
export function getStatusText(status) {
    return STATUS_LABELS[status] || status;
}

/**
 * Получить вариант цвета для статуса
 * @param {string} status - код статуса
 * @returns {string} - вариант Bootstrap
 */
export function getStatusVariant(status) {
    return STATUS_VARIANTS[status] || 'secondary';
}

/**
 * Получить описание статуса
 * @param {string} status - код статуса
 * @returns {string} - описание статуса
 */
export function getStatusDescription(status) {
    return STATUS_DESCRIPTIONS[status] || '';
}

/**
 * Получить список всех статусов для выбора
 * @returns {Array} - массив объектов статусов
 */
export function getAllStatuses() {
    return Object.values(REQUEST_STATUSES).map(status => ({
        key: status,
        label: STATUS_LABELS[status],
        variant: STATUS_VARIANTS[status],
        description: STATUS_DESCRIPTIONS[status]
    }));
}

/**
 * Проверить, является ли статус финальным
 * @param {string} status - код статуса
 * @returns {boolean}
 */
export function isFinalStatus(status) {
    return [
        REQUEST_STATUSES.COMPLETED,
        REQUEST_STATUSES.REJECTED,
        REQUEST_STATUSES.CANCELLED
    ].includes(status);
}

/**
 * Проверить, находится ли заявка на согласовании
 * @param {string} status - код статуса
 * @returns {boolean}
 */
export function isInApprovalProcess(status) {
    return [
        REQUEST_STATUSES.SUBMITTED,
        REQUEST_STATUSES.IN_REVIEW
    ].includes(status);
}

/**
 * Проверить, находится ли заявка в работе
 * @param {string} status - код статуса
 * @returns {boolean}
 */
export function isInProgress(status) {
    return [
        REQUEST_STATUSES.AWAITING_EXECUTOR,
        REQUEST_STATUSES.IN_PROGRESS
    ].includes(status);
}

export default {
    REQUEST_STATUSES,
    STATUS_LABELS,
    STATUS_VARIANTS,
    STATUS_DESCRIPTIONS,
    getStatusText,
    getStatusVariant,
    getStatusDescription,
    getAllStatuses,
    isFinalStatus,
    isInApprovalProcess,
    isInProgress
};

